import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import PropTypes from 'prop-types'

import { useProductDetails } from '@/common/hooks'

const classes = {
    wrapper: ({ wrapperHeight, wrapperWidth }) => ({
        width: wrapperWidth,
        height: wrapperHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),
    img: {
        height: '100%',
        width: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
    },
}

/**
 * Displays an organisation's logo or falls back to the product branding logo
 *
 * If an organisation has a custom logo (organisationLogoUri), it renders immediately.
 * If falling back to the default product logo, it waits for Split.io to resolve
 * the correct branding before rendering to prevent showing the wrong logo.
 *
 * @param {Object} props
 * @param {string} [props.organisationLogoUri] - Custom organisation logo URL
 * @param {Object|Array} [props.sx] - MUI sx prop for custom styling
 * @param {number|string} [props.wrapperHeight=100] - Height of the logo wrapper
 * @param {number|string} [props.wrapperWidth=100] - Width of the logo wrapper
 * @returns {React.ReactElement|null} Logo image or null while loading
 */
const OrganisationLogo = ({ organisationLogoUri, sx, wrapperHeight = 100, wrapperWidth = 100 }) => {
    const productDetails = useProductDetails()
    const defaultLogoUrl = productDetails.logo

    // State only for the async-fetched default logo
    const [fetchedDefaultLogo, setFetchedDefaultLogo] = useState(null)

    // Derive logo: prefer org logo prop, then fetched default, then fallback
    const logo = organisationLogoUri || fetchedDefaultLogo || defaultLogoUrl

    useEffect(() => {
        // Skip fetch if org has custom logo or branding not ready
        if (organisationLogoUri || !productDetails.isReady) {
            return
        }

        const controller = new AbortController()

        fetch(defaultLogoUrl, { signal: controller.signal })
            .then((response) => {
                setFetchedDefaultLogo(response.url)
            })
            .catch((err) => {
                // Ignore abort errors (expected on unmount)
                if (err.name !== 'AbortError') {
                    console.error('Failed to load default logo:', err)
                }
            })

        return () => {
            controller.abort()
        }
    }, [organisationLogoUri, productDetails.isReady, defaultLogoUrl])

    // If using default logo (no custom org logo), wait for branding to resolve
    // This prevents flashing the wrong logo while Split.io loads
    if (!organisationLogoUri && !productDetails.isReady) {
        return null
    }

    return (
        <Box sx={[classes.wrapper({ wrapperWidth, wrapperHeight }), ...(Array.isArray(sx) ? sx : [sx])]}>
            <Box
                alt="[Organisation Logo]"
                component="img"
                data-testid="organisation-logo"
                src={logo}
                sx={classes.img}
            />
        </Box>
    )
}
OrganisationLogo.propTypes = {
    organisationLogoUri: PropTypes.string,
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    wrapperHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    wrapperWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}
export default OrganisationLogo
